import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { VariableDto } from '@models/variable/variableDto.model';
import { VariableGroup } from '@models/variable/variableGroup.model';
import { EditorStateService } from '@services/editorState/editor-state.service';
import { VariableService } from '@services/variable/variable.service';
import { NgxSpinnerModule } from 'ngx-spinner';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-variables-panel',
  standalone: true,
  imports: [CommonModule,MatIconModule,MatButtonModule,NgxSpinnerModule,MatFormFieldModule,ReactiveFormsModule,MatProgressSpinnerModule,MatInputModule,MatTooltipModule,],
  templateUrl: './variables-panel.component.html',
  styleUrl: './variables-panel.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VariablesPanelComponent {
  searchControl = new FormControl('');
  variableGroups: VariableGroup[] = [];
  filteredGroups: VariableGroup[] = [];
  isLoading = false;
  expandedGroups = new Set<string>();

  private destroy$ = new Subject<void>();

  constructor(
    private variableService: VariableService,
    private editorStateService: EditorStateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.setupSearch();
    this.loadVariables();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((searchTerm: string | null) => {
        this.filterVariables(searchTerm || '');
      });
  }

  private loadVariables(): void {
    this.isLoading = true;

    this.variableService.getAllGroupedByType().subscribe({
      next: (groups) => {
        this.variableGroups = groups;
        this.filteredGroups = [...groups];
        this.isLoading = false;

        // Expandir el primer grupo por defecto
        if (groups.length > 0) {
          this.expandedGroups.add(groups[0].tipoVariable);
        }
      },
      error: (error) => {
        console.error('Error cargando variables:', error);
        this.isLoading = false;
      }
    });
  }

    onToggleFilters(): void {
    this.cdr.markForCheck();
  }

  private filterVariables(searchTerm: string): void {
    if (!searchTerm.trim()) {
      this.filteredGroups = [...this.variableGroups];
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    this.filteredGroups = this.variableGroups
      .map(group => ({
        ...group,
        variables: group.variables.filter((variable: VariableDto) =>
          variable.codigo.toLowerCase().includes(searchLower) ||
          (variable.descripcion?.toLowerCase().includes(searchLower)) ||
          (variable.valor?.toLowerCase().includes(searchLower))
        )
      }))
      .filter(group => group.variables.length > 0);

    // Expandir grupos que tienen resultados
    this.filteredGroups.forEach(group => {
      this.expandedGroups.add(group.tipoVariable);
    });
  }

    // ============ GETTERS PARA TEMPLATE ============
  get hasSearchErrors(): boolean {
    return this.searchControl.invalid && this.searchControl.touched;
  }

  get searchErrorMessage(): string {
    if (this.searchControl.hasError('script')) {
      return 'No se permiten scripts en la búsqueda';
    }
    if (this.searchControl.hasError('sql')) {
      return 'No se permiten comandos SQL en la búsqueda';
    }
    if (this.searchControl.hasError('maxlength')) {
      return 'Máximo 100 caracteres';
    }
    return '';
  }

  onVariableDoubleClick(variable: VariableDto): void {
    this.insertVariableInEditor(variable);
  }

  onVariableClick(variable: VariableDto): void {
    // Un solo click solo selecciona
    console.log('Variable seleccionada:', variable);
  }

  private insertVariableInEditor(variable: VariableDto): void {
    const activeTab = this.editorStateService.getActiveTab();
    if (!activeTab) {
      return;
    }

    // Insertar la variable en el contenido del tab activo
    const variableText = `{{${variable.codigo}}}`;
    const currentContent = activeTab.contenido;

    // Por ahora agregar al final, luego se puede mejorar para insertar en la posición del cursor
    const newContent = currentContent + variableText;

    this.editorStateService.updateTabContent(activeTab.id, newContent);
  }

  toggleGroup(groupName: string): void {
    if (this.expandedGroups.has(groupName)) {
      this.expandedGroups.delete(groupName);
    } else {
      this.expandedGroups.add(groupName);
    }
  }

  isGroupExpanded(groupName: string): boolean {
    return this.expandedGroups.has(groupName);
  }

  onRefresh(): void {
    this.loadVariables();
  }

  getVariableTooltip(variable: VariableDto): string {
    const parts: string[] = [
      `Código: {{${variable.codigo}}}`
    ];

    if (variable.descripcion) {
      parts.push(`Descripción: ${variable.descripcion}`);
    }

    if (variable.valor) {
      parts.push(`Valor: ${variable.valor}`);
    }

    return parts.join('\n');
  }
}
